'use client'

import { useEffect, useState, useCallback } from 'react'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Skeleton } from '@/components/ui/skeleton'
import { Icons } from '@/components/ui/icons'
import { apiClient } from '@/lib/api-client'
import { appendScopeFilterParams } from '@/lib/dashboard-scope-filters'
import { cn } from '@/lib/utils'

interface ParetoItem {
  policy_name: string
  priority: number
  fatal_count: number
  failure_count: number
  total_violations: number
  cumulative_pct: number
  is_critical: boolean
}

interface ParetoData {
  items: ParetoItem[]
  total_calls: number
  total_violations: number
  pareto_cutoff_index: number
  ai_insight: string | null
}

interface Props {
  startDate: string
  endDate: string
  targetEmployeeId?: number
  selectedProduct?: string
  selectedSite?: string
}

// Custom tooltip
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  const fatal = payload.find((p: { dataKey: string }) => p.dataKey === 'fatal_count')?.value ?? 0
  const failure = payload.find((p: { dataKey: string }) => p.dataKey === 'failure_count')?.value ?? 0
  const cumPct = payload.find((p: { dataKey: string }) => p.dataKey === 'cumulative_pct')?.value ?? 0
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg text-sm max-w-xs">
      <p className="font-semibold text-brand-navy mb-2 break-words">{label}</p>
      <p className="text-red-600">Fatal violations: <strong>{fatal}</strong></p>
      <p className="text-amber-600">Non-fatal failures: <strong>{failure}</strong></p>
      <p className="text-gray-500 border-t mt-1 pt-1">
        Total: <strong>{fatal + failure}</strong> &nbsp;|&nbsp; Cumulative: <strong>{cumPct}%</strong>
      </p>
    </div>
  )
}

export function ParetoQualityChart({ startDate, endDate, targetEmployeeId, selectedProduct, selectedSite }: Props) {
  const [data, setData] = useState<ParetoData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!startDate || !endDate) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
      if (targetEmployeeId) params.set('target_employee_id', String(targetEmployeeId))
      appendScopeFilterParams(params, selectedProduct, selectedSite)
      const res = await apiClient.get<ParetoData>(`/api/analysis/pareto-quality?${params}`)
      setData(res)
    } catch {
      setError('Failed to load pareto data')
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, targetEmployeeId, selectedProduct, selectedSite])

  useEffect(() => { load() }, [load])

  const chartData = (data?.items ?? []).map(item => ({
    ...item,
    short_name: item.policy_name.length > 18
      ? item.policy_name.slice(0, 16) + '…'
      : item.policy_name,
  }))

  return (
    <Card className="relative overflow-hidden">
      <CardWatermark opacity={2} />
      <CardHeader className="relative z-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Icons.trendingUp className="h-5 w-5 text-brand-cornflower" />
              Pareto Analysis — Quality Parameters
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Parameters causing 80% of violations highlighted · fix these first for maximum accuracy gain
            </p>
          </div>
          {data && (
            <div className="shrink-0 text-right">
              <p className="text-2xl font-bold text-brand-navy">{data.total_violations}</p>
              <p className="text-xs text-muted-foreground">total violations</p>
              <p className="text-xs text-muted-foreground">{data.total_calls} calls</p>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="relative z-10">
        {loading ? (
          <Skeleton className="h-72 w-full rounded-xl" />
        ) : error ? (
          <div className="flex items-center justify-center h-40 text-sm text-red-500 gap-2">
            <Icons.alertCircle className="h-4 w-4" />
            {error}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground gap-2">
            <Icons.activity className="h-8 w-8 opacity-30" />
            <p className="text-sm">No violation data for selected date range</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Legend chips */}
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 rounded-sm bg-red-500" />
                Fatal violations
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 rounded-sm bg-amber-400" />
                Non-fatal failures
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 rounded-full border-2 border-brand-cornflower bg-white" />
                Cumulative %
              </span>
              <span className="flex items-center gap-1.5 text-emerald-700 font-medium">
                <span className="inline-block h-0.5 w-5 bg-emerald-500 border-dashed border-t-2 border-emerald-500" />
                80% threshold
              </span>
            </div>

            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart
                data={chartData}
                margin={{ top: 5, right: 40, left: 0, bottom: 80 }}
                barCategoryGap="20%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="short_name"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  angle={-40}
                  textAnchor="end"
                  interval={0}
                  height={80}
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  allowDecimals={false}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine
                  yAxisId="right"
                  y={80}
                  stroke="#10b981"
                  strokeDasharray="6 3"
                  strokeWidth={2}
                  label={{ value: '80%', position: 'right', fontSize: 11, fill: '#10b981' }}
                />
                <Bar yAxisId="left" dataKey="fatal_count" stackId="a" fill="#ef4444" name="Fatal" radius={[0, 0, 0, 0]} />
                <Bar yAxisId="left" dataKey="failure_count" stackId="a" fill="#fbbf24" name="Non-fatal" radius={[3, 3, 0, 0]} />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cumulative_pct"
                  stroke="#4f6ef2"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#4f6ef2' }}
                  name="Cumulative %"
                />
                <Legend wrapperStyle={{ display: 'none' }} />
              </ComposedChart>
            </ResponsiveContainer>

            {/* Critical parameters summary */}
            <div className="flex flex-wrap gap-2 pt-1">
              {data.items.filter(i => i.is_critical).map(item => (
                <span
                  key={item.policy_name}
                  className={cn(
                    'px-2 py-1 rounded-full text-xs font-medium border',
                    item.fatal_count > 0
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : 'bg-amber-50 border-amber-200 text-amber-700'
                  )}
                >
                  {item.policy_name} ({item.total_violations})
                </span>
              ))}
            </div>

            {/* AI Insight */}
            {data.ai_insight && (
              <div className="rounded-lg bg-brand-cornflower/5 border border-brand-cornflower/20 p-4">
                <p className="text-xs font-semibold text-brand-cornflower mb-1 uppercase tracking-wide flex items-center gap-1">
                  <Icons.sparkles className="h-3 w-3" />
                  AI Insight
                </p>
                <p className="text-sm text-gray-700 leading-relaxed">{data.ai_insight}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
